// FLAGS: none
// Duration input: a preset picker plus an end-time mode that derives the minute count.
import { Input } from "@openup/shared/primitives/ui/input";
import FormField from "@src/components/spaces/FormField";
import {
  DURATION_OPTIONS,
  SessionFormState,
} from "@src/components/spaces/createEventForm";

interface SessionLengthFieldProps {
  session: SessionFormState;
  error?: string;
  onPatch: (partial: Partial<SessionFormState>) => void;
}

/** Length field: duration/end-time mode toggle plus the matching control. */
const SessionLengthField = ({
  session,
  error,
  onPatch,
}: SessionLengthFieldProps) => (
  <FormField label="Length" required error={error}>
    {({ id, invalid }) => (
      <div className="flex flex-col gap-2">
        <div
          role="group"
          aria-label="Length mode"
          className="flex w-fit divide-x divide-gray-300 overflow-hidden rounded-lg border border-gray-300 text-xs"
        >
          {(
            [
              ["duration", "Duration"],
              ["endTime", "End time"],
            ] as const
          ).map(([mode, label]) => (
            <button
              key={mode}
              type="button"
              aria-pressed={session.durationMode === mode}
              className={`px-3 py-1.5 font-medium transition-colors ${
                session.durationMode === mode
                  ? "bg-green-700 text-white"
                  : "bg-white text-gray-700 hover:bg-gray-100"
              }`}
              onClick={() => onPatch({ durationMode: mode })}
            >
              {label}
            </button>
          ))}
        </div>

        {session.durationMode === "duration" ? (
          <div
            role="group"
            aria-label="Duration"
            className="flex w-full divide-x divide-gray-300 overflow-hidden rounded-lg border border-gray-300"
          >
            {DURATION_OPTIONS.map((minutes) => (
              <button
                key={minutes}
                type="button"
                aria-pressed={session.durationMinutes === minutes}
                className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${
                  session.durationMinutes === minutes
                    ? "bg-green-700 text-white"
                    : "bg-white text-gray-700 hover:bg-gray-100"
                }`}
                onClick={() => onPatch({ durationMinutes: minutes })}
              >
                {minutes} min
              </button>
            ))}
          </div>
        ) : (
          <Input
            id={id}
            type="time"
            aria-label="End time"
            value={session.endTime}
            aria-invalid={invalid}
            onChange={(e) => onPatch({ endTime: e.target.value })}
          />
        )}
      </div>
    )}
  </FormField>
);

export default SessionLengthField;
